/**
 * Shared Details Components - Reusable UI patterns for detail widgets
 *
 * Extracted to enable DRY across RFQ, PO, and VendorResponse detail views.
 * Provides consistent status badges and vendor contact displays.
 *
 * @layer presentation
 * @arch_domain copilot
 */

import type { ReactNode } from 'react';
import { User, Loader2, Crosshair } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { ContactSummary } from '@/lib/api/types';

// ============================================================
// Status Badge Configuration
// ============================================================

export type StatusType = 'rfq' | 'po' | 'vendor_response';

interface StatusConfig {
  color: string;
  label: string;
}

const RFQ_STATUS_CONFIG: Record<string, StatusConfig> = {
  sent: { color: 'bg-info/20 text-info', label: 'sent' },
  draft: { color: 'bg-muted text-muted-foreground', label: 'draft' },
  done: { color: 'bg-success/20 text-success', label: 'done' },
  cancel: { color: 'bg-destructive/20 text-destructive', label: 'cancelled' },
};

const PO_STATUS_CONFIG: Record<string, StatusConfig> = {
  purchase: { color: 'bg-success/20 text-success', label: 'purchase' },
  draft: { color: 'bg-muted text-muted-foreground', label: 'draft' },
  sent: { color: 'bg-info/20 text-info', label: 'sent' },
  cancel: { color: 'bg-destructive/20 text-destructive', label: 'cancelled' },
};

const VENDOR_RESPONSE_STATUS_CONFIG: Record<string, StatusConfig> = {
  submitted: { color: 'bg-success/20 text-success', label: 'submitted' },
  draft: { color: 'bg-muted text-muted-foreground', label: 'draft' },
  revised: { color: 'bg-info/20 text-info', label: 'revised' },
};

const STATUS_CONFIGS: Record<StatusType, Record<string, StatusConfig>> = {
  rfq: RFQ_STATUS_CONFIG,
  po: PO_STATUS_CONFIG,
  vendor_response: VENDOR_RESPONSE_STATUS_CONFIG,
};

const DEFAULT_STATUS: StatusConfig = {
  color: 'bg-muted text-muted-foreground',
  label: 'unknown',
};

// ============================================================
// Status Badge Component
// ============================================================

interface StatusBadgeProps {
  status: string | null | undefined;
  type: StatusType;
  className?: string;
}

export function StatusBadge({ status, type, className }: StatusBadgeProps) {
  const config = STATUS_CONFIGS[type];
  const statusConfig = status ? config[status] || { ...DEFAULT_STATUS, label: status } : DEFAULT_STATUS;

  return (
    <span
      className={cn(
        'inline-block px-1.5 py-0.5 rounded text-[10px]',
        statusConfig.color,
        className
      )}
    >
      {statusConfig.label}
    </span>
  );
}

// ============================================================
// Platform Badge Configuration
// ============================================================

const PLATFORM_CONFIG: Record<string, { color: string; label: string }> = {
  whatsapp: { color: 'bg-success/20 text-success border-success/30', label: 'WhatsApp' },
  email: { color: 'bg-muted text-muted-foreground border-border', label: 'Email' },
  chatter: { color: 'bg-warning/20 text-warning border-warning/30', label: 'Messaging' },
  klyde_ai: { color: 'bg-primary/20 text-primary border-primary/30', label: 'Klyde AI' },
  ipe: { color: 'bg-primary/20 text-primary border-primary/30', label: 'IPE' },
};

const DEFAULT_PLATFORM = { color: 'bg-muted text-muted-foreground border-border', label: '' };

// ============================================================
// Platform Badge Component
// ============================================================

interface PlatformBadgeProps {
  platform: string;
  className?: string;
}

export function PlatformBadge({ platform, className }: PlatformBadgeProps) {
  const cfg = PLATFORM_CONFIG[platform] ?? { ...DEFAULT_PLATFORM, label: platform };

  return (
    <span className={cn('text-[10px] px-1.5 py-0.5 rounded-md border font-medium', cfg.color, className)}>
      {cfg.label}
    </span>
  );
}

// ============================================================
// Section Header Component
// ============================================================

interface SectionHeaderProps {
  title: string;
  count?: number;
  icon?: ReactNode;
}

export function SectionHeader({ title, count, icon }: SectionHeaderProps) {
  return (
    <div className="text-[10px] font-semibold text-muted-foreground mb-2 tracking-wider flex items-center gap-1.5">
      {icon}
      <span>
        {title}
        {count !== undefined && ` (${count})`}
      </span>
    </div>
  );
}

// ============================================================
// Vendor Contacts List Component
// ============================================================

interface VendorContactsListProps {
  contacts: ContactSummary[];
  isLoading?: boolean;
  maxDisplay?: number;
  emptyMessage?: string;
}

export function VendorContactsList({
  contacts,
  isLoading = false,
  maxDisplay = 3,
  emptyMessage = 'No contacts',
}: VendorContactsListProps) {
  if (isLoading) {
    return (
      <div className="mt-2 ml-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading contacts...
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="mt-1 ml-6 text-xs text-muted-foreground/60 italic">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="mt-2 ml-6 space-y-1">
      {contacts.slice(0, maxDisplay).map((contact) => (
        <div
          key={contact.semantic_id}
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <User className="h-3 w-3" />
          <span>{contact.name}</span>
          {contact.phone && (
            <span className="text-muted-foreground/60">({contact.phone})</span>
          )}
        </div>
      ))}
      {contacts.length > maxDisplay && (
        <div className="text-xs text-muted-foreground/60">
          +{contacts.length - maxDisplay} more
        </div>
      )}
    </div>
  );
}

// ============================================================
// Loading State Component
// ============================================================

interface LoadingStateProps {
  message?: string;
}

export function LoadingState({ message = 'Loading...' }: LoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
      <Loader2 className="h-8 w-8 mb-2 animate-spin" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ============================================================
// Line Items Table Component
// ============================================================

interface LineItem {
  cdk_id?: string | null;
  cdk?: string | null;
  product?: { name: string } | null;
  product_name?: string | null;
  product_sku?: string | null;
  description?: string | null;
  quantity?: number | null;
  quantity_offered?: number | null;
  unit_price?: number | null;
  total?: number | null;
  // The requirement (Intent LineItem / schedule swimlane) this candidate
  // was picked to satisfy — several LineItems can share one
  // source_line_sid (one requirement, N vendor candidates). Only present
  // on cost-plan line items (costplan_details) — see
  // `costplan_line_items_from_graph_rows` in
  // klyde_edge/klyde_intelligence/projects/queries/costplan_loaders.py.
  source_line_sid?: string | null;
  // Live-joined human label for source_line_sid (e.g. "Coffee table").
  source_line_label?: string | null;
}

interface LineItemsTableProps {
  items: LineItem[];
  showTotal?: boolean;
  priceColumn?: 'unit_price' | 'total';
  priceLabel?: string;
  // Opt-in click-to-focus affordance — OFF by default so mode1's
  // existing `mcp_details` rail usage (card-registry.tsx) stays exactly
  // as it is today. The Cost Plan rail surface (mode3/mode11,
  // cost-plan-surface.tsx) is the only caller that turns this on.
  interactive?: boolean;
  focusedSourceLineSid?: string | null;
  onRowClick?: (item: LineItem) => void;
  // The entity's own server-computed total (RFQ/PO amount_total, MCP's
  // costplan_details amount_total) — already arithmetic the BFF did once,
  // over the SAME line items being rendered here. Passing it is the
  // whole fix: summing item.total again client-side was redundant, not
  // "just formatting" — the server had already decided the number,
  // this component was quietly re-deciding it a second time. No fetch
  // cost to reading it instead; it's already in the same response.
  // Falls back to a client-side sum ONLY when omitted — the one real
  // exception being a caller with no server round-trip in the loop at
  // all yet (a draft cart's own preview total).
  total?: number | null;
}

export function LineItemsTable({
  items,
  showTotal = false,
  priceColumn = 'unit_price',
  // Default matches priceColumn's default — 'Price' alone reads as a
  // line total (natural to assume next to Qty), but it's the per-unit
  // figure. Found live: the MCP details panel shows this un-overridden,
  // same ambiguity VendorResponseLine.unit_price already needed calling
  // out this session.
  priceLabel = 'Unit Price',
  total,
  interactive = false,
  focusedSourceLineSid = null,
  onRowClick,
}: LineItemsTableProps) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No line items
      </div>
    );
  }

  const grandTotal = total ?? items.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-muted border-b border-border">
              {interactive && <th className="w-5 px-1 py-1.5" aria-hidden />}
              <th className="text-left px-1.5 py-1.5 font-medium">Product</th>
              <th className="text-right px-1.5 py-1.5 font-medium">Qty</th>
              <th className="text-right px-1.5 py-1.5 font-medium">{priceLabel}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const price = priceColumn === 'total' ? item.total : item.unit_price;
              const isFocused = interactive
                && !!item.source_line_sid
                && item.source_line_sid === focusedSourceLineSid;
              return (
                <tr
                  key={item.cdk_id || item.cdk || idx}
                  onClick={interactive ? () => onRowClick?.(item) : undefined}
                  className={cn(
                    'border-b border-border last:border-0',
                    interactive && 'cursor-pointer hover:bg-muted',
                    isFocused && 'bg-primary/5',
                  )}
                >
                  {interactive && (
                    <td className="px-1 py-1.5 text-center">
                      <Crosshair
                        className={cn(
                          'h-3 w-3 inline-block',
                          isFocused ? 'text-primary' : 'text-muted-foreground/40',
                        )}
                        aria-hidden
                      />
                    </td>
                  )}
                  <td className={cn('px-1.5 py-1.5 truncate max-w-[120px]', isFocused && 'border-l-2 border-l-primary')}>
                    {item.product?.name || item.product_name || item.product_sku || item.description || 'Line Item'}
                  </td>
                  <td className="text-right px-1.5 py-1.5">{item.quantity ?? item.quantity_offered ?? '-'}</td>
                  <td className="text-right px-1.5 py-1.5">
                    {price != null ? `$${price.toFixed(0)}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showTotal && (
        <div className="flex justify-between items-center mt-2 px-1.5 text-xs">
          <span className="text-muted-foreground">Total</span>
          <span className="font-semibold">${grandTotal.toFixed(0)}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Odoo Link Component
// ============================================================

interface OdooLinkProps {
  odooId: number;
  model: 'purchase.order' | 'res.partner' | 'product.template' | 'project.project';
  label: string;
}

export function OdooLink({ odooId, model, label }: OdooLinkProps) {
  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  return (
    <a
      href={`${ODOO_HOST}/web#id=${odooId}&model=${model}&view_type=form`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 w-full p-2 rounded-lg border border-border hover:bg-accent transition-colors text-xs"
    >
      <span className="h-3 w-3 text-muted-foreground">↗</span>
      <span className="truncate">{label}</span>
    </a>
  );
}
